import React, { useCallback } from 'react';
import {
  IconTypeahead,
  StaticTypeahead,
  C,
  openConfirm,
} from '@kineticdata/bundle-common';
import { I18n, Moment, slugify } from '@kineticdata/react';
import { DefaultActions } from './';
import { Markdown } from '../Markdown';
import {
  CONTENT_MANAGER_MODE,
  SHORT_DESCRIPTION_MAX_LENGTH,
  getStatusColor,
} from '../helpers';
import classNames from 'classnames';
import moment from 'moment';

const AppendixSettingsRenderer = ({
  mode,
  content,
  values,
  activeItem,
  handleContentChangeDirect,
  handleValuesChange,
  handleValuesChangeChecked,
  handleValuesChangeDirect,
  handleContentDelete,
  handleResetSection,
  actionsPending,
  imageDir,
}) => {
  const isViewMode = mode !== CONTENT_MANAGER_MODE.EDIT;
  const id = activeItem && activeItem.id;

  const handleShortDescriptionBlur = useCallback(
    e => {
      const el = e.target;
      const value = (el.value || '').trim();
      if (value.length > SHORT_DESCRIPTION_MAX_LENGTH) {
        const newValue = value.slice(0, SHORT_DESCRIPTION_MAX_LENGTH);
        openConfirm({
          title: 'Truncate Short Description?',
          body: (
            <div>
              <I18n>
                Your short description is too long and will be truncated to{' '}
                {SHORT_DESCRIPTION_MAX_LENGTH} characters:
              </I18n>{' '}
              <em>"{newValue}"</em>
            </div>
          ),
          actionName: 'Continue and Truncate',
          ok: () => {
            handleValuesChangeDirect(id, 'Short Description')(newValue);
          },
          cancel: () => {
            el.focus();
          },
        });
      }
    },
    [handleValuesChangeDirect, id],
  );

  return !!activeItem && mode !== CONTENT_MANAGER_MODE.PUBLIC ? (
    <>
      <h2>
        <I18n>{activeItem.title}</I18n>
      </h2>

      <div className="form form-unstyled">
        <div className="h6">
          <span
            className={`badge badge-${getStatusColor(
              values.get('Status'),
            )} badge-stylized text-muted`}
          >
            <I18n>{values.get('Status')}</I18n>
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="appendix-name" className="field-label">
            <I18n>Appendix Name</I18n>
          </label>
          <input
            type="text"
            id="appendix-name"
            value={values.get('Name')}
            onChange={handleValuesChange(id, 'Name')}
            disabled={isViewMode}
          />
        </div>

        <div className="form-group">
          <label htmlFor="appendix-desc" className="field-label">
            <I18n>Short Description</I18n>
          </label>
          <textarea
            type="text"
            id="appendix-desc"
            value={values.get('Short Description') || ''}
            onChange={handleValuesChange(id, 'Short Description')}
            onBlur={handleShortDescriptionBlur}
            disabled={isViewMode}
            rows="3"
          />
          <small className="d-flex p-1">
            <span className="flex-grow-1 text-muted">
              <I18n>
                {`This short description will be shown in list views of appendices. Max ${SHORT_DESCRIPTION_MAX_LENGTH} characters.`}
              </I18n>
            </span>
            <span
              className={classNames('flex-shrink-0 ml-2', {
                'text-danger':
                  (values.get('Short Description') || '').length >
                  SHORT_DESCRIPTION_MAX_LENGTH,
              })}
            >
              {SHORT_DESCRIPTION_MAX_LENGTH -
                (values.get('Short Description') || '').length}{' '}
              / {SHORT_DESCRIPTION_MAX_LENGTH}
            </span>
          </small>
        </div>

        <div className="checkbox">
          <label className="field-label">
            <I18n>Type</I18n>
          </label>
          {AppendixSettingsRenderer.FIELDS['Type'].options.map(
            ({ label, value }, i) => (
              <label htmlFor={slugify(value)} key={slugify(value)}>
                <input
                  name="type-radio-options"
                  type="radio"
                  id={slugify(value)}
                  checked={values.get('Type') === value}
                  onChange={handleValuesChangeChecked(id, 'Type', value)}
                  disabled={isViewMode}
                />
                <I18n>{label}</I18n>
              </label>
            ),
          )}
        </div>

        <div className="form-group">
          <label htmlFor="exp-date" className="field-label">
            <I18n>Expiration Date</I18n>
          </label>
          <input
            type="text"
            id="exp-date"
            value={
              values.get(C.FIELD_EXPIRATION_DATE)
                ? moment(values.get(C.FIELD_EXPIRATION_DATE)).format(
                    Moment.formats.date,
                  )
                : 'None'
            }
            disabled={true}
          />
          <small className="d-flex p-1">
            <I18n>
              The date when the content expires and will need to be updated.
              This date is set during the approval process.
            </I18n>
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="portfolio-icon" className="field-label">
            <I18n>Icon</I18n>
          </label>
          <IconTypeahead
            value={values.get('Icon')}
            onChange={handleValuesChangeDirect(id, 'Icon')}
            minSearchLength={0}
            id="portfolio-icon"
            disabled={isViewMode}
          />
        </div>

        <div className="form-group">
          <label htmlFor="appendix-keywords" className="field-label">
            <I18n>Keywords</I18n>
          </label>
          <StaticTypeahead
            options={[
              ...values
                .get('Name', '')
                .split(' ')
                .filter(Boolean),
            ].map(value => ({ value }))}
            value={(values.get('Keywords') || []).map(value => ({ value }))}
            onChange={vals =>
              handleValuesChangeDirect(id, 'Keywords')(
                vals.toJS().map(kw => kw.value),
              )
            }
            search={{ fields: [{ name: 'value', operator: 'matches' }] }}
            allowNew={true}
            messages={{
              custom:
                'No suggested keywords available. Type and press enter to add custom values.',
            }}
            components={{
              Input: isViewMode
                ? (values.get('Keywords') || []).length > 0
                  ? () => null
                  : () => (
                      <div className="form-control-plaintext">
                        <em>None</em>
                      </div>
                    )
                : undefined,
            }}
            multiple={true}
            minSearchLength={0}
            id="appendix-keywords"
            disabled={isViewMode}
          />
        </div>

        <div className="form-group">
          <label htmlFor="grade" className="field-label">
            <I18n>Grade</I18n>
          </label>
          <select
            id="grade"
            value={values.get('Grade') || ''}
            onChange={handleValuesChange(id, 'Grade')}
            disabled={isViewMode}
          >
            <option />
            <option value="1">1 (New Appendix)</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10 (Complete)</option>
          </select>
          <small className="d-flex p-1">
            <I18n>
              The grade defining the readiness level of the content. Should be
              set manually during the revision process before submitting for
              approval.
            </I18n>
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="content">
            <I18n>Notes</I18n>
          </label>
          <Markdown
            initialValue={activeItem.value}
            onChange={handleContentChangeDirect(id, 'value')}
            minHeight="200px"
            disabled={isViewMode}
            imageDir={imageDir}
            autofocus={false}
          />
        </div>

        {!isViewMode && (
          <DefaultActions
            id={id}
            handleResetSection={handleResetSection}
            isDirty={activeItem.isDirty}
            actionsPending={actionsPending}
          />
        )}
      </div>
    </>
  ) : null;
};

// Metadata for this renderer
AppendixSettingsRenderer.ID = 'APPENDIX_SETTINGS';
AppendixSettingsRenderer.NAME = 'Appendix Settings';

// Define fields managed by this section so we can know which fields to reset
// when the section is reset. Can store additional metadata for fields to use in
// the renderer above.
AppendixSettingsRenderer.FIELDS = {
  Name: {},
  'Short Description': {},
  Icon: {},
  Type: {
    options: [
      { label: 'Public', value: 'Public' },
      { label: 'Internal', value: 'Internal' },
    ],
  },
  Grade: {},
  Keywords: {},
};

export default AppendixSettingsRenderer;
